import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  archiveSchema,
  createSchema,
  deleteSchema,
  getSchemas,
  restoreSchema,
  updateSchema,
  setSchemaAutomation,
} from '../../structured-data/structuredDataApi';

// Draft shape the editor works on (and the API accepts). Every field carries
// options + itemFields defaults so dirty comparison (JSON.stringify) stays
// stable between a saved schema and its untouched draft.
export function schemaToDraft(s = {}) {
  return {
    name: s.name || '',
    description: s.description || '',
    targetTypes: Array.isArray(s.targetTypes) ? s.targetTypes : [],
    targetCategories: Array.isArray(s.targetCategories) ? s.targetCategories : [],
    fields: Array.isArray(s.fields)
      ? s.fields.map((f) => ({
        options: [],
        ...f,
        itemFields: Array.isArray(f.itemFields) ? f.itemFields.map((sf) => ({ options: [], ...sf })) : [],
      }))
      : [],
    promptPreamble: s.promptPreamble || '',
    model: s.model || '',
    includeRelatedSources: !!s.includeRelatedSources,
    includeDrafts: !!s.includeDrafts,
    includeTerminated: !!s.includeTerminated,
    active: s.active !== false,
  };
}

export const slugifyKey = (label) => String(label || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .replace(/^([0-9])/, '_$1')
  .slice(0, 48);

// Duplicate-key detection drives both the inline row errors and the disabled
// Save button.
export function duplicateKeys(fields = []) {
  const seen = new Set();
  const dupes = new Set();
  for (const f of fields) {
    const key = (f.key || '').trim();
    if (!key) continue;
    if (seen.has(key)) dupes.add(key);
    seen.add(key);
  }
  return dupes;
}

// Owns list + per-schema drafts for the Data Schemas editor. Drafts are keyed
// by schema id (client ids for unsaved "New schema" drafts) so switching in
// the rail never drops edits — same posture as the playbook editors.
export default function useDataSchemas({ showToast }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [models, setModels] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCounter, setNewCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getSchemas()
      .then((res) => {
        if (cancelled) return;
        const list = res.schemas || [];
        setSchemas(list);
        setModels(res.models || []);
        // Land on a live schema — archived ones sit in a collapsed section.
        setSelectedId((list.find((s) => s.active) || list[0])?.id || null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const byId = useMemo(() => new Map(schemas.map((s) => [s.id, s])), [schemas]);
  const isNewId = (id) => String(id || '').startsWith('new-');

  const draftFor = useCallback((id) => {
    if (drafts[id]) return drafts[id];
    const base = byId.get(id);
    return base ? schemaToDraft(base) : null;
  }, [drafts, byId]);

  const setDraft = useCallback((id, patch) => {
    setDrafts((prev) => {
      const current = prev[id] || schemaToDraft(byId.get(id) || {});
      return { ...prev, [id]: { ...current, ...patch } };
    });
  }, [byId]);

  const isDirty = useCallback((id) => {
    const draft = drafts[id];
    if (!draft) return false;
    if (isNewId(id)) return true;
    const base = byId.get(id);
    return !base || JSON.stringify(draft) !== JSON.stringify(schemaToDraft(base));
  }, [drafts, byId]);

  const anyDirty = useMemo(
    () => Object.keys(drafts).some((id) => isDirty(id)),
    [drafts, isDirty]
  );

  const addNew = useCallback(() => {
    const id = `new-${newCounter + 1}`;
    setNewCounter((n) => n + 1);
    setDrafts((prev) => ({
      ...prev,
      [id]: schemaToDraft({ name: '', model: models[0]?.value || '', fields: [] }),
    }));
    setSelectedId(id);
  }, [newCounter, models]);

  const reset = useCallback((id) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (isNewId(id)) {
      setSelectedId(schemas[0]?.id || null);
    }
  }, [schemas]);

  const save = useCallback(async (id) => {
    const draft = draftFor(id);
    if (!draft) return;
    setSaving(true);
    try {
      const saved = isNewId(id)
        ? await createSchema(draft)
        : await updateSchema(id, draft);
      setSchemas((prev) => {
        // Save/archive responses don't carry outputCount (list-only field) —
        // keep the count we already have so the delete guard stays informed.
        const prior = prev.find((s) => s.id === saved.id);
        const rest = prev.filter((s) => s.id !== saved.id);
        return [...rest, { ...saved, outputCount: prior?.outputCount ?? 0 }]
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setSelectedId(saved.id);
      showToast('Schema saved', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }, [draftFor, showToast]);

  // Archive / restore (doc-89): server flips only the active flag (never the
  // version). An in-flight draft's active field is patched in step so the
  // toggle alone doesn't read as an unsaved edit.
  const setActive = useCallback(async (id, active) => {
    try {
      const saved = active ? await restoreSchema(id) : await archiveSchema(id);
      setSchemas((prev) => prev.map((s) => (
        s.id === saved.id ? { ...saved, outputCount: s.outputCount ?? 0 } : s
      )));
      setDrafts((prev) => (
        prev[id] ? { ...prev, [id]: { ...prev[id], active: saved.active } } : prev
      ));
      showToast(active ? 'Schema restored' : 'Schema archived', 'success');
      return true;
    } catch (err) {
      showToast(err.message, 'error');
      return false;
    }
  }, [showToast]);

  // Hard delete (doc-89). The server guards (409 while outputs exist) — the
  // UI offers archive first, but a stale count can't slip a delete through.
  const remove = useCallback(async (id) => {
    try {
      await deleteSchema(id);
      const next = schemas.filter((s) => s.id !== id);
      setSchemas(next);
      setDrafts((prev) => {
        const rest = { ...prev };
        delete rest[id];
        return rest;
      });
      setSelectedId((sel) => (
        sel === id ? ((next.find((s) => s.active) || next[0])?.id || null) : sel
      ));
      showToast('Schema deleted', 'success');
      return true;
    } catch (err) {
      showToast(err.message, 'error');
      return false;
    }
  }, [schemas, showToast]);

  // Rail entries: saved schemas (already alphabetical from the API, kept
  // sorted on save) followed by unsaved new drafts. Archived schemas render
  // in their own collapsed section below the live ones.
  const railItems = useMemo(() => ([
    ...schemas.filter((s) => s.active).map((s) => ({
      id: s.id, name: s.name, active: true, isNew: false,
    })),
    ...Object.keys(drafts).filter(isNewId).map((id) => ({
      id, name: drafts[id]?.name || 'Untitled schema', active: true, isNew: true,
    })),
  ]), [schemas, drafts]);

  const archivedItems = useMemo(
    () => schemas.filter((s) => !s.active).map((s) => ({ id: s.id, name: s.name, active: false, isNew: false })),
    [schemas]
  );

  const setAutomation = async (id, autoRun) => {
    const saved = await setSchemaAutomation(id, autoRun);
    setSchemas((prev) => prev.map((s) => s.id === id ? { ...s, autoRun: saved.autoRun } : s));
  };

  return {
    loading, error, models, schemas, byId, setAutomation,
    railItems, archivedItems, selectedId, setSelectedId,
    draftFor, setDraft, isDirty, anyDirty, isNewId,
    addNew, reset, save, saving, setActive, remove,
  };
}
